import React, { Dispatch, SetStateAction, useState, useMemo } from "react";
import {
  colorsLight,
  H5,
  InlineButton,
  Body2,
  SingleSelectDropdown,
  MultipleSelectDefaultAll,
  SelectOptionsHook,
  useLazyLoadingQueryOptionsHook,
} from "@coherehealth/common";
import { CASE_HISTORICAL_PRODUCTION_DATE_FILTER_OPTIONS } from "../util/ChartUtil";
import { DateFilterOptions } from "../util/ChartUtil";
import { UseSearchUserProps, useSearchUser, useGetSkills, User as QMAgent, Queue, User } from "@coherehealth/qm-api";
import config from "api/config";

interface ProductivityFiltersProps {
  dateValue: DateFilterOptions;
  changeQueryStringValue: (newDateValue: DateFilterOptions, newQueue: string | undefined) => void;
  skills: string[];
  setSkills: Dispatch<SetStateAction<string[]>>;
  agents: QMAgent[];
  setAgents: Dispatch<SetStateAction<QMAgent[]>>;
  queue: string | undefined;
  queues: Queue[];
  clear: () => void;
}

const ProductivityFilters = ({
  dateValue,
  changeQueryStringValue,
  skills,
  setSkills,
  agents,
  setAgents,
  queue,
  queues,
  clear,
}: ProductivityFiltersProps) => {
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [agentsOpen, setAgentsOpen] = useState(false);
  const { data: allSkills } = useGetSkills({});

  const skillsOptions = useMemo(() => {
    return allSkills ? allSkills.map((skill) => skill.name || "").filter((skill) => !!skill) : [];
  }, [allSkills]);
  const useSkillsOptions: SelectOptionsHook<string> = ({ query }) => {
    let options = query
      ? skillsOptions.filter((skill) => skill.toLowerCase().includes(query.toLocaleLowerCase()))
      : skillsOptions;
    return {
      options,
      optionsLoading: false,
    };
  };

  const useQmSearchUser = (props: UseSearchUserProps) =>
    useSearchUser({ ...props, base: `${config.QM_SERVICE_API_URL}` });
  const useUsers: SelectOptionsHook<QMAgent> = (selectOptionsParams) =>
    useLazyLoadingQueryOptionsHook({
      ...selectOptionsParams,
      useGetHook: useQmSearchUser,
      additionalQueryParams: {
        name: selectOptionsParams.query,
      },
    });

  const queueOptions = queues.map((queue) => ({ id: queue.id || "", label: queue.name || "" }));

  const isQueueSkillBased = useMemo(() => {
    const faxIndex = queues
      .find((q) => q.id === queue)
      ?.filters?.findIndex((filter) => filter.filterValue === "FAX_INTAKE");
    return faxIndex ? faxIndex < 0 : true;
  }, [queue, queues]);

  return (
    <div
      style={{
        flex: "0 0 316px",
        height: "100%",
        backgroundColor: colorsLight.background.card,
        boxShadow: "0px 2px 2px rgba(207, 226, 231, 0.8)",
        padding: "26px 24px",
      }}
    >
      <div
        style={{
          display: "inline-flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <H5>Filters</H5>
        <InlineButton onClick={clear} style={{ padding: 0 }}>
          <Body2>Clear All</Body2>
        </InlineButton>
      </div>
      <SingleSelectDropdown
        label={"Queue"}
        value={queue}
        onChange={(val) => changeQueryStringValue(dateValue, val as string)}
        options={queueOptions}
        style={{ marginTop: "60px" }}
      />
      <MultipleSelectDefaultAll
        values={agents}
        setValues={setAgents}
        style={{ marginTop: "26px" }}
        useOptions={useUsers}
        label={"Agent"}
        open={agentsOpen}
        setOpen={setAgentsOpen}
        getOptionLabel={(user: User) => user.name || ""}
      />
      <MultipleSelectDefaultAll
        values={skills}
        setValues={setSkills}
        style={{ marginTop: "26px" }}
        useOptions={useSkillsOptions}
        label={"Skills"}
        open={skillsOpen}
        setOpen={setSkillsOpen}
        disabled={!isQueueSkillBased}
      />
      <SingleSelectDropdown
        label={"Time period"}
        value={dateValue}
        onChange={(val) => changeQueryStringValue(val as DateFilterOptions, queue)}
        options={CASE_HISTORICAL_PRODUCTION_DATE_FILTER_OPTIONS}
        style={{ marginTop: "26px" }}
      />
    </div>
  );
};

export default ProductivityFilters;
